/**
 * JewelleryTagView — PUBLIC (no-auth) QR-scan landing page at /t/:id.
 *
 * Reached by scanning a printed tag's QR code. The tag is PASSWORD-GATED: the
 * visitor must enter the universal tag password before any data is fetched or
 * shown. The password is verified on the BACKEND (X-Tag-Password header), so a
 * wrong/missing password returns 403 and reveals nothing — the gate cannot be
 * bypassed from the client.
 *
 * On success it renders the SAME <JewelleryTagViewer> used in-app (layouts,
 * collapse/expand, print). Never shows any price / value.
 *
 * WHITE-LABEL: carries NO portal branding/identity, and uses a bare axios call
 * (NOT the app `api` client) so a missing session never bounces to login.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API_BASE } from '@/lib/api';
import { JewelleryTagViewer, buildTagDataFromPublic } from '@/components/jewellery/JewelleryTagViewer';
import { PublicPricedTag, PublicTotalValue, usePublicPrice } from '@/components/jewellery/PublicPricedTag';
import { setPricing } from '@/lib/tagPricing';
import { setGoldTyped } from '@/lib/goldRate';

// QR tag: plain, plain + total sale value under it, or the single-column priced layout.
function PricedOrPlain({ data }) {
  const price = usePublicPrice(data);
  if (price?.mode === 'lines') return <PublicPricedTag data={data} price={price} />;
  return (
    <>
      <JewelleryTagViewer data={data} lockPublic publicView={data.view} />
      {price ? <PublicTotalValue price={price} /> : null}
    </>
  );
}
import { setTagRules, setTagClassOrder } from '@/lib/tagRules';

export default function JewelleryTagView() {
  const { id } = useParams();
  const [state, setState] = useState('checking'); // checking | locked | gate | ok | notfound | error
  const [data, setData] = useState(null);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pwError, setPwError] = useState('');

  const path = `/public/jewellery-tag/${id}`;
  const sameOriginBase = (typeof window !== 'undefined' ? `${window.location.origin}/api` : API_BASE);

  // Fetch the tag with the entered password. Served same-origin so nothing
  // leaks the portal domain; retries the configured backend ONLY on a genuine
  // transport error (no HTTP response) — never on an authoritative 403/404.
  const fetchTag = async (pw) => {
    const headers = { 'X-Tag-Password': pw };
    try {
      return await axios.get(`${sameOriginBase}${path}`, { headers });
    } catch (e) {
      const differsFromBackend = API_BASE && !`${sameOriginBase}${path}`.startsWith(API_BASE);
      if (differsFromBackend && !e?.response) {
        return axios.get(`${API_BASE}${path}`, { headers });
      }
      throw e;
    }
  };

  useEffect(() => {
    axios.get(`${sameOriginBase}/public/portal-status`)
      .then(({ data: s }) => setState(s?.locked ? 'locked' : s?.partial ? 'notfound' : 'gate'))
      .catch(() => setState('gate'));
  }, [sameOriginBase]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pw = password.trim();
    if (!pw || verifying) return;
    setVerifying(true);
    setPwError('');
    try {
      const res = await fetchTag(pw);
      setTagRules(res.data.tag_rules);
      setTagClassOrder(res.data.tag_class_order);
      const tag = buildTagDataFromPublic(res.data);
      if (res.data.view && res.data.view.show_image === false) tag.image = null;
      setPricing({ stoneFromCard: true });
      if (res.data.pricing?.gold) setGoldTyped(String(res.data.pricing.gold.rate));
      setData({ ...tag, charges: res.data.pricing?.charges || [], view: res.data.view, pricing: res.data.pricing || null });
      setState('ok');
    } catch (err) {
      const st = err?.response?.status;
      if (st === 423) { setState('locked'); window.dispatchEvent(new Event('tagws-locked')); }
      else if (st === 403) setPwError('Incorrect password. Please try again.');
      else if (st === 404) setState('notfound');
      else setState('error');
    } finally {
      setVerifying(false);
    }
  };

  const resetToGate = () => { setState('gate'); setPwError(''); };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-start py-6 px-4">
      <div className="w-full max-w-xl">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 sm:p-5">
          {state === 'checking' && (
            <div className="flex justify-center py-12" data-testid="public-tag-checking"><Loader2 className="animate-spin text-slate-400" /></div>
          )}
          {state === 'locked' && (
            <div className="flex flex-col items-center gap-3 py-10 px-2 text-center" data-testid="public-tag-locked">
              <p className="text-base font-semibold text-slate-800 sm:text-lg" data-testid="public-tag-locked-message">The site is under maintenance, We will be back soon.</p>
            </div>
          )}
          {state === 'gate' && (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col items-center text-center gap-4 py-8 px-2"
              data-testid="public-tag-gate"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Lock size={22} className="text-slate-500" aria-hidden="true" />
              </div>
              <div>
                <div className="text-base font-semibold text-slate-800">Protected tag</div>
                <p className="mt-1 text-sm text-slate-500">Enter the password to view this jewellery tag.</p>
              </div>
              <div className="w-full max-w-xs text-left">
                <Label htmlFor="tag-password" className="text-[12px] text-slate-600">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="tag-password"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (pwError) setPwError(''); }}
                    placeholder="Enter password"
                    autoFocus
                    autoComplete="off"
                    aria-invalid={!!pwError}
                    data-testid="public-tag-password-input"
                    className={`pr-10 ${pwError ? 'border-rose-400 focus-visible:ring-rose-400' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {pwError ? (
                  <div className="mt-2 flex items-center gap-1.5 text-[12px] text-rose-600" data-testid="public-tag-pw-error">
                    <AlertCircle size={13} /> {pwError}
                  </div>
                ) : null}
              </div>
              <Button
                type="submit"
                disabled={verifying || !password.trim()}
                data-testid="public-tag-submit"
                className="w-full max-w-xs bg-slate-900 hover:bg-slate-800 text-white"
              >
                {verifying ? (<><Loader2 size={15} className="mr-1.5 animate-spin" /> Verifying…</>) : 'View tag'}
              </Button>
            </form>
          )}

          {state === 'notfound' && (
            <div className="flex flex-col items-center text-center gap-2 py-14" data-testid="public-tag-notfound">
              <AlertCircle size={28} className="text-slate-300" />
              <div className="text-sm font-medium text-slate-700">Data does not exist</div>
              <div className="text-[12px] text-slate-500">This jewellery tag link is invalid or the data does not exist.</div>
            </div>
          )}

          {state === 'error' && (
            <div className="flex flex-col items-center text-center gap-3 py-14" data-testid="public-tag-error">
              <AlertCircle size={28} className="text-rose-300" />
              <div className="text-sm font-medium text-slate-700">Could not load tag</div>
              <div className="text-[12px] text-slate-500">Please check your connection and try again.</div>
              <Button variant="outline" onClick={resetToGate} data-testid="public-tag-retry" className="mt-1">Try again</Button>
            </div>
          )}

          {state === 'ok' && data ? <PricedOrPlain data={data} /> : null}
        </div>
      </div>
    </div>
  );
}
